import { Checkbox, DefaultButton, DirectionalHint, Icon, IPanelStyles, IStackTokens, ITextFieldStyles, Link, mergeStyles, Panel, PanelType, PrimaryButton, ScrollablePane, ScrollbarVisibility, Stack, TextField, TooltipDelay, TooltipHost } from "@fluentui/react";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ContentManagerCopyMoveModeEnum } from "../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { setContentManagerMoveTo } from '../../src/taskpane/redux/modules/contentManagerDirectory';
import { ChangedInput } from "../../src/taskpane/components/file/fileItems";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { IFileToUpload } from "../../src/taskpane/interfaces/IFileToUpload";
import { setFiles, updateFileName } from "../../src/taskpane/redux/modules/file";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { CMConfirmModal } from "./CMConfirmModal";

export const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 135px)",
});

const themedLargeStackTokens: IStackTokens = {
    childrenGap: "s2",
    padding: "0px 0px"
};

const textFieldStyles: Partial<ITextFieldStyles> = {
    suffix: {
        paddingLeft: 0
    }
};

const stackTokens: IStackTokens = { childrenGap: 5 };

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 15,
        paddingRight: 15
    }
}
export interface ICMEmailAttachmentPanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
}

export const CMEmailAttachmentPanel = ({ panelIsOpen, closePanel }: ICMEmailAttachmentPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const VISIBLE_FILES_PER_LOAD = 30;
    const MAX_SELECTED_FILES = 50;

    const dispatch = useDispatch();

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const files = useSelector((state: RootState) => state.files.files);

    const [showConfirmMoveToDialog, setShowConfirmMoveToDialog] = React.useState<boolean>(false);
    const [visibleFiles, setVisibleFiles] = React.useState<any[]>([]);
    const [showMaxFilesSelectedDialog, setShowMaxFilesSelectedDialog] = React.useState<boolean>(false);

    const isOneDrive = currentDirectory?.type === ComponentTypes.OneDrive || currentDirectory?.type === ComponentTypes.OneDriveSubFolder;

    React.useEffect(() => {
        if (files) {
            if (visibleFiles.length === 0) {
                setVisibleFiles(files.filter(item => item.isGraphMailItem).slice(0, VISIBLE_FILES_PER_LOAD));
            } else {
                setVisibleFiles(files.filter(item => item.isGraphMailItem).slice(0, visibleFiles.length));
            }
        }
    }, [files]);

    const addMoreVisibleFiles = () => {
        setVisibleFiles(files.filter(item => item.isGraphMailItem).slice(0, visibleFiles.length + VISIBLE_FILES_PER_LOAD));
    }
    const handleTextFieldOnchange = async (event: any, newValue: string): Promise<any> => {
        const currentChangedInput: ChangedInput = {
            id: event.target.name,
            value: newValue
        }
        dispatch(updateFileName({ name: currentChangedInput.value, id: currentChangedInput.id }));
    };

    const getFileErrorMessage = (file: IFileToUpload) => {
        if (file.filePreCommitResult) {
            if (file.filePreCommitResult.exists && !file.filePreCommitResult.locked && !file.filePreCommitResult.checkedOut)
                return languageStrings.FileSameName;
            if (file.filePreCommitResult.exists && file.filePreCommitResult.locked && !file.filePreCommitResult.checkedOut)
                return languageStrings.FileSameLocked;
            if (file.filePreCommitResult.exists && !file.filePreCommitResult.locked && file.filePreCommitResult.checkedOut)
                return languageStrings.FileSameCheckedOut;
        }

        return "";
    };

    const handleEmailChecked = async (event, isChecked: boolean, item: any) => {
        dispatch(setFiles(files.map(file => {
            return {
                ...file,
                isSelected: item.id === file.id ? isChecked : file.isSelected
            }
        })))
    };

    const onRenderFooterContent = React.useCallback(
        () => (
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
                {!generalModule.isInConentMangerMode() && <PrimaryButton iconProps={{ iconName: 'Save' }} onClick={() => {
                    if (files.filter(x => x.isSelected && x.isGraphMailItem).length > MAX_SELECTED_FILES) {
                        setShowMaxFilesSelectedDialog(true);
                    } else {
                        setShowConfirmMoveToDialog(true);
                    }
                }
                }
                    text={languageStrings.Save} />}
                <DefaultButton
                    iconProps={{ iconName: 'Checkmark' }}
                    onClick={() => {
                        closePanel();
                    }}
                    text={languageStrings.Close}
                />
            </Stack>
        ),
        [files],
    );

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText={"Emails"}
            onDismiss={() => closePanel()}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "350px"}
            styles={panelStyles}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            <div className={scrollStyle} >
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <Stack tokens={stackTokens} style={{ marginTop: '15px' }}>
                        {visibleFiles.map((item, index) => {
                            let mailClass = "xb-select-documents-mail-border xb-select-documents-mail";
                            let attachmentClass = "xb-select-documents-mail-border xb-select-documents-attachment";
                            return (
                                <div className={!item.isAttachment ? mailClass : attachmentClass} key={item.id}>
                                    <Stack verticalAlign={"center"} horizontal horizontalAlign={"stretch"} tokens={themedLargeStackTokens}>
                                        <Icon
                                            className="iconMailImage"
                                            {...getFileTypeIconProps({
                                                extension: item.extension,
                                                size: 16,
                                                imageFileType: "png"
                                            })}
                                        />
                                        <Stack.Item grow>
                                            <TooltipHost
                                                content={item.name}
                                                directionalHint={DirectionalHint.topLeftEdge}
                                                delay={TooltipDelay.long}
                                            >
                                                {item.isAttachment ? (
                                                    <TextField
                                                        errorMessage={item.isDuplicate || item?.filePreCommitResult?.checkedOut || item?.filePreCommitResult?.locked ? getFileErrorMessage(item) : ""}
                                                        disabled={!item.isSelected || (currentDirectory?.type === ComponentTypes.Saving && files.filter(x => x.isSelected === true).length === 1)}
                                                        styles={textFieldStyles}
                                                        borderless
                                                        value={item.name}
                                                        name={item.id}
                                                        onChange={handleTextFieldOnchange}
                                                        suffix={item.extension ? `.${item.extension}` : undefined}
                                                        className="xb-select-documents-input"
                                                    />
                                                ) : (
                                                    <TextField
                                                        errorMessage={item.isDuplicate || item?.filePreCommitResult?.checkedOut || item?.filePreCommitResult?.locked ? getFileErrorMessage(item) : ""}
                                                        disabled={!item.isSelected || (currentDirectory?.type === ComponentTypes.Saving && files.filter(x => x.isSelected === true).length === 1)}
                                                        styles={textFieldStyles}
                                                        borderless
                                                        name={item.id}
                                                        value={item.name}
                                                        onChange={handleTextFieldOnchange}
                                                        className="xb-select-documents-input"
                                                    />
                                                )}
                                            </TooltipHost>
                                        </Stack.Item>
                                        <Checkbox
                                            key={item.name}
                                            name={item.id}
                                            checked={item.isSelected}
                                            className="mailCheckbox"
                                            onChange={(ev, checked) => handleEmailChecked(ev, checked, item)}
                                        />
                                    </Stack>
                                </div>
                            )
                        })}
                        {files.filter(x => x.isGraphMailItem).length > visibleFiles.length &&
                            <div style={{ paddingTop: "15px", textAlign: "center" }}>
                                <Link onClick={() => addMoreVisibleFiles()}>{languageStrings.LoadMore}</Link>
                            </div>
                        }
                    </Stack>
                </ScrollablePane>
                {showConfirmMoveToDialog && !generalModule.isInConentMangerMode() && <CMConfirmModal
                    title={languageStrings.SaveSelectedFiles}
                    message={languageStrings.SelectDestinationDirectory}
                    showDialog={showConfirmMoveToDialog}
                    closeDialog={() => {
                        setShowConfirmMoveToDialog(false);
                    }}
                    closeText=""
                    confirmText={languageStrings.Ok}
                    onConfirm={() => {
                        dispatch(setContentManagerMoveTo({
                            mode: ContentManagerCopyMoveModeEnum.CopySelectedEmails,
                            error: "",
                            moveToEnabled: true,
                            workingDirectories: [],
                            selectedFiles: [],
                            siteUrl: "",
                            sourceSiteUrl: "",
                            sourceServerRelPath: "",
                            busyMovingFiles: false,
                            copyJobResults: [],
                            isMoveComplete: false,
                            isMoveCancelled: false,
                            destinationDirectory: {},
                            progressCountCompleted: 0,
                            progressCountTotal: 0
                        }));
                        dispatch(setFiles(files.map(file => {
                            return {
                                ...file,
                                isSelected: !file.isGraphMailItem ? false : file.isSelected
                            }
                        })));
                        setShowConfirmMoveToDialog(false);
                        closePanel();
                    }}
                />}
                {showMaxFilesSelectedDialog && <CMConfirmModal
                    title={languageStrings.LimitReached}
                    message={languageStrings.MaximumEmailMessagesSelected.replace("{amount}", MAX_SELECTED_FILES.toString())}
                    showDialog={showMaxFilesSelectedDialog}
                    closeDialog={() => {
                        setShowMaxFilesSelectedDialog(false);
                    }}
                    closeText=""
                    confirmText={languageStrings.Ok}
                    onConfirm={() => {
                        setShowMaxFilesSelectedDialog(false);
                    }}
                />}
            </div>

        </Panel>
    )
};